import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "./useCurrentUser";
import type { Database } from "@/integrations/supabase/types";
import { pickOrganization } from "@/lib/organization/pickOrganization";

/** Legacy (per-browser) key, still read as a fallback and kept in step for other tabs. */
const ORGANIZATION_STORAGE_KEY = "zettl:current_organization_id";
/** The last company each person used, so signing in opens it again (per user, per browser). */
const userOrgKey = (userId: string) => `${ORGANIZATION_STORAGE_KEY}:${userId}`;

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / blocked storage: the choice just isn't remembered.
  }
}

export type OrgRole = Database["public"]["Enums"]["org_role"];

export interface Organization {
  id: string;
  name: string;
  role: OrgRole;
  logo_url: string | null;
  created_at: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  setCurrentOrganization: (org: Organization) => void;
  /** True when the current user is owner or admin of the current organization. */
  isCurrentOrgAdmin: boolean;
  /**
   * True until the current company is known: while the user, the membership
   * list, or the selection is still resolving. Pages show a skeleton then,
   * never "no data", and org-scoped queries wait (`enabled: !!orgId`).
   */
  isLoading: boolean;
  refetch: () => Promise<{ data: Organization[] | undefined }>;
}

const ROLE_ORDER: Record<OrgRole, number> = { owner: 0, admin: 1, member: 2 };

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isPending: userPending } = useCurrentUser();

  const {
    data: organizations = [],
    isPending: orgsPending,
    refetch,
  } = useQuery({
    queryKey: ["organizations", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: memberships, error } = await supabase
        .from("organization_members")
        .select("role, organization:organizations(id, name, logo_url, created_at)")
        .eq("user_id", user.id);

      // Throw, don't return []: an empty list is cached as "this person has no
      // company" and the app opened empty until the next refetch. A thrown
      // error is retried.
      if (error) {
        console.error("[useOrganization] Error fetching memberships:", error);
        throw error;
      }

      const orgs: Organization[] = (memberships ?? [])
        .filter((m) => m.organization)
        .map((m) => ({
          id: m.organization.id,
          name: m.organization.name,
          role: m.role,
          logo_url: m.organization.logo_url,
          created_at: m.organization.created_at,
        }));

      return orgs.sort(
        (a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || a.name.localeCompare(b.name)
      );
    },
    enabled: !!user,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // The explicit choice in this tab; null = use the stored / default one. The
  // current company is derived in the same render as the list arrives, so no
  // render ever sees "orgs loaded, none selected" and queries never start with
  // an undefined id.
  const [chosenId, setChosenId] = useState<string | null>(null);
  const currentOrganization = useMemo(
    () =>
      pickOrganization(organizations, [
        chosenId,
        user ? readStored(userOrgKey(user.id)) : null,
        readStored(ORGANIZATION_STORAGE_KEY),
      ]),
    [organizations, chosenId, user]
  );

  // Remember what is open (per user, and the legacy key other tabs listen to).
  useEffect(() => {
    if (!currentOrganization || !user) return;
    writeStored(userOrgKey(user.id), currentOrganization.id);
    writeStored(ORGANIZATION_STORAGE_KEY, currentOrganization.id);
  }, [currentOrganization, user]);

  // A different person signed in: forget this tab's choice.
  useEffect(() => {
    setChosenId(null);
  }, [user?.id]);

  // Org-scoped query keys embed the org id (see src/lib/queryKeys.ts), so
  // switching needs no cache invalidation — new keys, fresh queries.
  const setCurrentOrganization = (org: Organization) => {
    setChosenId(org.id);
  };

  // Follow org switches made in other tabs.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== ORGANIZATION_STORAGE_KEY) return;
      const org = organizations.find((o) => o.id === e.newValue);
      if (org) setChosenId(org.id);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [organizations]);

  const isLoading = userPending || (!!user && orgsPending);

  const isCurrentOrgAdmin =
    currentOrganization?.role === "owner" || currentOrganization?.role === "admin";

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        setCurrentOrganization,
        isCurrentOrgAdmin,
        isLoading,
        refetch,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
